import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/categories';
import { trustBadges } from '@/mocks/homeContent';

export default function HeroSection() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <section className="relative w-full min-h-screen flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Warm%20minimalist%20lifestyle%20flatlay%20composition%20with%20laptop%20displaying%20clean%20spreadsheet%20interface%20surrounded%20by%20premium%20accessories%20watch%20leather%20goods%20and%20coffee%20on%20a%20cream%20marble%20surface%2C%20soft%20golden%20morning%20sunlight%20from%20left%20window%2C%20warm%20beige%20sand%20and%20cream%20tones%2C%20editorial%20photography%20style%2C%20sophisticated%20clean%20composition%2C%20professional%20catalog%20aesthetic%2C%20subtle%20natural%20shadows&width=1600&height=900&seq=hero-main-v2-june2026&orientation=landscape"
          alt="AllChinaBuy Spreadsheet Directory - Premium curated shopping resources and guides"
          title="AllChinaBuy Spreadsheet Directory Product Showcase"
          className="w-full h-full object-cover object-top"
          loading="eager"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/35"></div>

      <div className="relative z-10 w-full max-w-4xl mx-auto px-4 text-center mt-10 md:mt-0">
        <span className="inline-block px-4 py-1.5 border border-background-50/30 rounded-full text-background-50 text-xs md:text-sm font-medium mb-6 tracking-wide">
          Free Directory 2026
        </span>

        <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl text-background-50 font-bold leading-tight mb-6">
          The Largest AllChinaBuy<br className="hidden md:block" /> Spreadsheet Directory
        </h1>

        <p className="text-background-50/85 text-sm md:text-base lg:text-lg max-w-2xl mx-auto mb-10 leading-relaxed">
          Discover trusted spreadsheets, curated sellers, product categories, and updated buying guides for AllChinaBuy users worldwide.
          Your complete resource for smarter, faster, and more organized shopping.
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10">
          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 text-background-50 rounded-full text-sm md:text-base font-semibold hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
          >
            allchinabuy spreadsheet
            <i className="ri-arrow-right-up-line"></i>
          </a>

          <div ref={dropdownRef} className="relative">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-background-50/15 backdrop-blur-sm border border-background-50/30 text-background-50 rounded-full text-sm md:text-base font-medium hover:bg-background-50/25 transition-all whitespace-nowrap cursor-pointer"
            >
              Explore Categories
              <i className={`ri-arrow-down-s-line transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {dropdownOpen && (
              <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 w-64 bg-background-50 rounded-xl border border-background-200/80 py-2 max-h-72 overflow-y-auto z-20">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
                    onClick={() => setDropdownOpen(false)}
                  >
                    {cat.name}
                  </a>
                ))}
              </div>
            )}
          </div>

          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-accent-500 text-background-50 rounded-full text-sm md:text-base font-semibold hover:bg-accent-600 transition-all whitespace-nowrap cursor-pointer"
          >
            Buy It Now
            <i className="ri-shopping-bag-line"></i>
          </a>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-4 md:gap-8">
          {trustBadges.map((badge) => (
            <div key={badge.label} className="flex items-center gap-2 text-background-50/80">
              <span className="w-5 h-5 flex items-center justify-center">
                <i className={`${badge.icon} text-sm`}></i>
              </span>
              <span className="text-xs md:text-sm whitespace-nowrap">{badge.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}